import { useEffect } from 'react';

import moment from 'moment';

import { fetchAndSetUserDetails } from '../context/thunks/userThunks';
import { useAppDispatch, useAppSelector } from './hooks';

// Give the backend's activation job a moment after the scheduled time.
const AFTER_ACTIVATION_MS = 60 * 1000;
// The activation job can lag; once the time has passed, keep checking.
const RETRY_AFTER_ACTIVATION_MS = 5 * 60 * 1000;
// setTimeout overflows past ~24.8 days (fires at once): wait at most this
// long, then re-arm.
const MAX_WAIT_MS = 6 * 60 * 60 * 1000;

/**
 * A scheduled tier plan (TMG-3904) can keep the undismissable "Plan Activation
 * Scheduled" card up until the new plan activates. The app only loads user
 * details at launch, so one left open across `scheduledPlanActivateAt` would
 * stay blocked until reopened. This refetches them just after the activation
 * time, and every few minutes after it while the backend still reports the
 * plan as scheduled.
 *
 * Each check arms the next one itself: a refetch that returns the same
 * timestamp (before activation, or while the backend job lags) leaves the
 * effect's dependency unchanged, so the effect alone would never re-run. The
 * chain ends when the refetch clears the date (dependency change → cleanup)
 * or the component unmounts.
 */
const useRefreshOnScheduledPlanActivation = () => {
  const dispatch = useAppDispatch();
  const activateAt = useAppSelector(
    (state) => state.user.scheduledPlanActivateAt,
  );

  useEffect(() => {
    if (!activateAt) return undefined;
    const activation = moment(activateAt);
    if (!activation.isValid()) return undefined;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const nextDelay = () => {
      const untilActivation = activation.diff(moment()) + AFTER_ACTIVATION_MS;
      return untilActivation > 0
        ? Math.min(untilActivation, MAX_WAIT_MS)
        : RETRY_AFTER_ACTIVATION_MS;
    };

    const arm = () => {
      timer = setTimeout(async () => {
        try {
          await dispatch(fetchAndSetUserDetails());
        } finally {
          // Still scheduled (same date) or the fetch failed: check again.
          if (!cancelled) arm();
        }
      }, nextDelay());
    };
    arm();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [activateAt, dispatch]);
};

export default useRefreshOnScheduledPlanActivation;
